import { CommonModule, DatePipe } from '@angular/common';
import { Component, HostListener, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { OrganizationService } from '../../../../core/services/organization/organization.service';
import { ToastService } from '../../../../core/services/toast.service';
import { ListColumnConfig } from 'app/main/components/list-page-config/list-page.config';
import { PatientListModel } from '../../../../core/models/patient/patient-list.model';
import { Organization } from 'app/core/models/organization/organization.model';
import { ListCardItem } from 'app/main/components/list-card/list-card.component';
import { DynamicListConfig } from 'app/core/models/table/dynamic-list.model';
import { DynamicListComponent } from 'app/shared/components/dynamic-list/dynamic-list.component';
import { Patient } from 'app/core/models/patient/patient.model';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { PageSizeSelectorComponent } from 'app/shared/components/page-size-selector/page-size-selector.component';
import { HasClaimDirective } from 'app/shared/directives/has-claim.directive';
import { BaseListComponent } from 'app/shared/base/base-list.component';
import { ListStateStackService } from 'app/core/services/list/list-state-stack.service';

@Component({
  selector: 'app-patient-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    DynamicListComponent,
    SelectComponent,
    PageSizeSelectorComponent,
    HasClaimDirective,
  ],
  templateUrl: './patient-list.component.html',
  styleUrl: './patient-list.component.scss',
  providers: [DatePipe],
})
export class PatientListComponent extends BaseListComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private patientsService: PatientsService,
    private currentUserService: CurrentUserService,
    private signalRService: SignalRService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    public permissionService: PermissionService,
    private organizationService: OrganizationService,
    private toastService: ToastService,
    listStateStack: ListStateStackService,
  ) {
    super(listStateStack, 'patient-list');
    this.collapseElementId = 'collapseExample';
    const lang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(lang);
  }

  patientConfig: DynamicListConfig = {
    columns: [
      {
        key: 'fullName',
        labelKey: 'patientNameSurname',
        type: 'user',
      },
      { key: 'bsnNumber', labelKey: 'patientID', type: 'text' },
      {
        key: 'patientOrganization',
        labelKey: 'createdDate',
        type: 'date',
        transform: (val) => val?.createdDate || '-',
      },
      {
        key: 'patientOrganization',
        labelKey: 'organizationCode',
        type: 'text',
        transform: (val) => val?.organizationName || '-',
      },
      { key: 'actions', labelKey: 'ACTIONS', type: 'actions' },
    ],
    actions: [
      {
        icon: 'fa-regular fa-pen-to-square',
        title: 'edit',
        permission: 'update_patient_claim',
        onClick: (item) => this.onEdit(item),
      },
      {
        icon: 'fa-regular fa-trash-can',
        title: 'delete',
        permission: 'delete_patient_claim',
        onClick: (item) => this.onDeleteCard(item),
      },
    ],
  };

  patients: PatientListModel[] = [];
  patientCards: ListCardItem[] = [];

  totalPatient = 0;
  totalPage = 0;
  pageSizes = [10, 15, 25, 50, 100];
  patientListLoading = false;

  /* FILTER */
  override filterForm: {
    name: string;
    pid: string;
    startDate: string | null;
    endDate: string | null;
    organizationId: string | null;
    status: string;
    sortBy?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
  } = {
    name: '',
    pid: '',
    startDate: null,
    endDate: null,
    organizationId: null,
    status: '',
    sortBy: null,
    sortDirection: null,
  };

  organizations: Organization[] = [];
  isSystemAdmin = false;

  deleteModalId = '';

  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  override ngOnInit(): void {
    super.ngOnInit();
    // Only load data if state wasn't restored (base class already called loadData if state was restored)
    if (!this.stateWasRestored) {
      this.getPatientList(this.currentPage, this.pageSize, this.filterForm);
    }
    this.createPatientListConnection();
    this.checkUserRoleAndLoadOrganizations();
  }

  ngAfterViewInit(): void {
    this.restoreCollapseState();
    this.setupCollapseListener();
  }

  private setupCollapseListener(): void {
    if (!this.collapseElementId) {
      return;
    }

    const collapseElement = document.getElementById(this.collapseElementId);
    if (!collapseElement) {
      return;
    }

    collapseElement.addEventListener('show.bs.collapse', () => {
      this.updateCollapseState(true);
    });

    collapseElement.addEventListener('hide.bs.collapse', () => {
      this.updateCollapseState(false);
    });
  }

  private checkUserRoleAndLoadOrganizations(): void {
    const currentUser = this.currentUserService.GetSession();
    this.isSystemAdmin = currentUser?.role?.name === 'System Admin';

    if (this.isSystemAdmin) {
      this.organizationService.getAllOrganizationItems().subscribe({
        next: (orgs) => {
          this.organizations = orgs || [];
        },
        error: (err) => {
          console.error('Error loading organizations:', err);
          this.organizations = [];
        },
      });
    }
  }



  onSort(column: string) {
    if (this.filterForm.sortBy === column) {
      if (this.filterForm.sortDirection === 'asc') {
        this.filterForm.sortDirection = 'desc';
      } else {
        this.filterForm.sortBy = null;
        this.filterForm.sortDirection = null;
      }
    } else {
      this.filterForm.sortBy = column;
      this.filterForm.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.getPatientList(this.currentPage, this.pageSize);
  }

  clearFilters() {
    this.filterForm = {
      name: '',
      pid: '',
      startDate: null,
      endDate: null,
      status: '',
      organizationId: null,
      sortBy: null,
      sortDirection: null,
    };
    this.getPatientList(1, this.pageSize);
  }

  /* ------------------ API ------------------ */

  getPatientList(page: number, size: number, filters?: any): void {
    this.patientListLoading = true;
    const apiIndex = page - 1;
    const apiFilters: any = {};

    if (filters) {
      if (filters.name) {
        apiFilters.name = filters.name;
      }
      if (filters.pid) {
        apiFilters.pid = filters.pid;
      }
      if (filters.startDate) {
        apiFilters.startDate = filters.startDate;
      }
      if (filters.endDate) {
        apiFilters.endDate = filters.endDate;
      }
      if (filters.status) {
        apiFilters.status = filters.status;
      }
      if (filters.organizationId) {
        apiFilters.organizationId = filters.organizationId;
      }
      if (filters.sortBy) {
        apiFilters.sortBy = filters.sortBy;
      }
      if (filters.sortDirection) {
        apiFilters.sortDirection = filters.sortDirection;
      }
    }

    this.patientsService.getListPatient(apiIndex, size, apiFilters).subscribe({
      next: (res) => {
        const data = res.data;

        this.patients = data.items.map((p: any) => ({
          ...p,
          fullName: `${p.name || ''} ${p.surname || ''}`.trim(),
        }));
        this.patientCards = data.items.map((p: any) => ({
          ...p,
          fullName: `${p.name || ''} ${p.surname || ''}`.trim(),
          organizationName: p.patientOrganization?.organizationName || '-',
        }));

        if (this.filterForm.sortBy) {
          const isDesc = this.filterForm.sortDirection === 'desc';
          const multiplier = isDesc ? -1 : 1;

          this.patientCards.sort((a: any, b: any) => {
            let valA: any;
            let valB: any;

            if (this.filterForm.sortBy === 'createdDate') {
              valA = new Date(
                a.patientOrganization?.createdDate || a.createdDate || 0
              ).getTime();
              valB = new Date(
                b.patientOrganization?.createdDate || b.createdDate || 0
              ).getTime();
            } else if (this.filterForm.sortBy === 'patientName') {
              valA = (a.fullName || '').toLowerCase();
              valB = (b.fullName || '').toLowerCase();
            } else if (this.filterForm.sortBy === 'organizationCode') {
              valA = (a.organizationCode || '').toLowerCase();
              valB = (b.organizationCode || '').toLowerCase();
            } else {
              return 0;
            }

            if (valA < valB) return -1 * multiplier;
            if (valA > valB) return 1 * multiplier;
            return 0;
          });
          this.patients.sort((a: any, b: any) => {
             let valA: any;
            let valB: any;

            if (this.filterForm.sortBy === 'createdDate') {
              valA = new Date(
                a.patientOrganization?.createdDate || a.createdDate || 0
              ).getTime();
              valB = new Date(
                b.patientOrganization?.createdDate || b.createdDate || 0
              ).getTime();
            } else if (this.filterForm.sortBy === 'patientName') {
              valA = (a.fullName || '').toLowerCase();
              valB = (b.fullName || '').toLowerCase();
            } else if (this.filterForm.sortBy === 'organizationCode') {
              valA = (a.patientOrganization?.organizationName || '').toLowerCase();
              valB = (b.patientOrganization?.organizationName || '').toLowerCase();
            } else {
              return 0;
            }
             if (valA < valB) return -1 * multiplier;
            if (valA > valB) return 1 * multiplier;
            return 0;
          });
        }

        this.totalPatient = data.count;
        this.totalPage = data.pages;
        this.currentPage = page;

        this.patientListLoading = false;
      },
      error: () => {
        this.patientListLoading = false;
      },
    });
  }

  /* ------------------ SIGNALR ------------------ */

  private createPatientListConnection() {
    const hubUrl = `${environment.apiLocations.patientHub}/patient-hub`;

    this.signalRService.connect(hubUrl);

    this.signalRService.on('receivePatientCreated', (message) => {
      const patient: PatientListModel = JSON.parse(message);

      if (patient?.id) {
        this.patients = [patient, ...this.patients];
        this.patientCards = this.patients.map((p) => this.mapToListCardItem(p));
        this.totalPatient++;
      }
    });
  }

  /* ------------------ MAPPING ------------------ */

  private mapToListCardItem(patient: any): ListCardItem {
    const genderVal = patient.gender || patient.Gender;
    const orgName = patient.patientOrganization?.organizationName || '-';
    const createdDate =
      patient.patientOrganization?.createdDate || patient.createdDate || '';

    return {
      id: patient.id || patient.Id,
      name: `${patient.name || patient.Name || ''} ${
        patient.surname || patient.Surname || ''
      }`.trim(),
      email: patient.email || patient.Email || '-',
      patientID: patient.bsnNumber || patient.recordId || '-',
      age: patient.age || patient.Age || 0,
      gender: genderVal === 2 ? 'M' : genderVal === 3 ? 'F' : '',
      phone: patient.phoneNumber || patient.PhoneNumber || '-',
      location: patient.placeOfResidence || patient.PlaceOfResidence || '-',
      birthDate: patient.dateOfBirth || patient.DateOfBirth || '',
      createdDate: createdDate,
      organizationCode: orgName,
      organization: orgName,
      role: '-',
      lastLoginDate: '',
      patientStatus: patient.status || 1,
    };
  }

  /* ------------------ UI ------------------ */

  changePage(page: number) {
    if (page > 0 && page <= this.totalPage) {
      this.getPatientList(page, this.pageSize, this.filterForm);
    }
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getPatientList(this.currentPage, this.pageSize, this.filterForm);
  }



  onCardClick(card: Patient) {
    if (this.permissionService.hasPermission('get_patient_by_id_claim')) {
      this.router.navigate(['/patient/detail', card.id]);
    }
  }

  getColClass(col: ListColumnConfig): string {
    switch (col.colType) {
      case 'user':
        return 'col-user';
      case 'dataEmail':
        return 'col-data-email';
      case 'actions':
        return 'col-actions';
      default:
        return 'col-data';
    }
  }

  onEdit(card: ListCardItem) {
    this.router.navigate(['/patient/edit', card.id]);
  }

  onDeleteCard(card: ListCardItem) {
    this.deleteModalId = String(card.id);
    const modalElement = document.getElementById('deletePatientModal');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deletePatient(id: string): void {
    if (id) {
      this.patientsService.deletePatient(id).subscribe({
        next: () => {
          this.toastService.showToast(
            this.translate.instant('successDeletePatientDesc'),
            'success',
          );
          this.getPatientList(this.currentPage, this.pageSize, this.filterForm);
        },
        error: () => {
          this.toastService.showToast(
            this.translate.instant('errorDeletePatientDesc'),
            'error',
          );
        },
      });
    }
  }

  validateDateInput(event: Event, fieldName: 'startDate' | 'endDate'): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      this.filterForm[fieldName] = null;
      return;
    }
    this.filterForm[fieldName] = input.value;
  }

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const x = rect.left;
    const y = rect.bottom + 4;

    this.pendingTooltipPosition = { x, y };

    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: translatedText,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  override loadData(): void {
    this.getPatientList(this.currentPage, this.pageSize, this.filterForm);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
